import { loadWeeklyCalendarCounts } from './components/mobileCalendarCounts';
import MobileJobActions from './components/MobileJobActions';
import useMobileSchedule from '../../hooks/useMobileSchedule';
import MobileScheduleMoveDialog from './components/MobileScheduleMoveDialog';
// src/components/schedule/SiteMeasureSchedule.js
import { memo, useCallback, useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
    FiMove, FiCalendar, FiChevronLeft, FiChevronRight,
    FiX, FiHash, FiUser, FiPackage, FiChevronsLeft, FiChevronsRight,
    FiSearch,
    FiMapPin,
    FiEdit2,
    FiTrash2,
    FiPlus,
    FiMessageSquare
} from "react-icons/fi";
import { MapIcon, PartyPopper, Ruler } from "lucide-react";

import {
    startOfWeek, endOfWeek, addWeeks, format,
    isBefore, startOfDay, isToday, addDays, differenceInWeeks
} from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DatePickerStyles.css';
import { useHolidays } from '../../hooks/useHolidays';
import TextTooltip from '../common/TextTooltip';
import {
    DndContext, DragOverlay, useSensor, useSensors, PointerSensor,
    pointerWithin, closestCenter, MeasuringStrategy, useDroppable,
    defaultDropAnimationSideEffects,
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import {
    SortableContext, useSortable,
    verticalListSortingStrategy, rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useVirtualizer } from '@tanstack/react-virtual';
import ConfirmationPopup from "../common/ConfirmationPopup";
import { DayColumnSkeleton, UnscheduledSectionSkeleton } from "../common/SkeletonLoader";
import {
    getSiteMeasureScheduled,
    getSiteMeasureUnscheduled,
    updateSiteMeasureDate,
    updateSiteMeasureConfirmed,
    updateQuoteQuickNotes
} from "../../lib/supabase/schedule";
import {
    subscribeToJobUpdates,
    subscribeToJobQuotesUpdates,
    unsubscribeFromJobUpdates,
} from "../../lib/supabase/realtime";
import BackButton from "../common/BackButton";
import Toast from "../common/Toast";
import ScheduleActivityLog from "./ScheduleActivityLog";
import { createJobActionLog } from "../../lib/supabase/activityLog";
import { createPortal } from "react-dom";

// ─── Collision detection ──────────────────────────────────────────────────────
// Prioritise the unscheduled drop zone over day columns (same problem as QuoteSchedule:
// day column overflow-y scroll rects extend beyond the visible area).
const siteMeasureCollision = (args) => {
    const px = args.pointerCoordinates?.x ?? 0;
    const py = args.pointerCoordinates?.y ?? 0;
    const zoneId = 'unscheduled-zone';
    const container = args.droppableContainers.find(c => c.id === zoneId && !c.disabled);
    if (container) {
        const rect = container.rect.current;
        if (rect && px >= rect.left && px <= rect.right && py >= rect.top && py <= rect.bottom) {
            return [{ id: zoneId }];
        }
    }
    const results = pointerWithin(args);
    return results.length ? results : closestCenter(args);
};

// ─── Constants ────────────────────────────────────────────────────────────────
const QUOTE_CONFIG = {
    roof: { label: 'Roof', bg: 'bg-blue-100', text: 'text-blue-700' },
    wall: { label: 'Wall', bg: 'bg-purple-100', text: 'text-purple-700' },
    floor: { label: 'Floor', bg: 'bg-green-100', text: 'text-green-700' },
    timber: { label: 'Timber', bg: 'bg-amber-100', text: 'text-amber-700' },
};

// const DESIGN_DOT = {
//     completed: 'bg-green-500',
//     in_progress: 'bg-yellow-400',
//     waiting_assignment: 'bg-gray-300',
//     waiting_design: 'bg-gray-300',
//     not_required: 'bg-gray-200',
//     not_started: 'bg-gray-200',
// };

// const INVOICE_STYLES = {
//     not_invoiced: 'text-red-800 bg-red-200',
//     invoiced: 'text-green-800 bg-green-200',
//     cod: 'text-green-800 bg-green-200',
//     deposit: 'text-yellow-800 bg-yellow-200',
//     paid: 'text-green-800 bg-green-200',
// };

// const getMainStatusColor = (status) => {
//     switch (status) {
//         case 'pending_design': return 'bg-yellow-500';
//         case 'in_design': return 'bg-blue-500';
//         case 'design_completed': return 'bg-green-500';
//         case 'pending_estimation': return 'bg-orange-500';
//         case 'in_estimation': return 'bg-purple-500';
//         case 'estimation_completed': return 'bg-emerald-500';
//         case 'ready_for_delivery': return 'bg-teal-500';
//         case 'completed': return 'bg-gray-500';
//         default: return 'bg-gray-400';
//     }
// };



// ─── Module-level droppable zone ──────────────────────────────────────────────
const DroppableUnscheduled = ({ children }) => {
    const { isOver, setNodeRef } = useDroppable({ id: 'unscheduled-zone' });
    return (
        <div
            ref={setNodeRef}
            className={`bg-white border-2 border-dashed rounded-xl p-6 transition-colors
                ${isOver ? 'border-orange-300 bg-orange-50' : 'border-gray-300 hover:border-orange-300'}`}
        >
            {children}
        </div>
    );
};

const formatNoteDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) +
        ' at ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const QuickNotesButton = ({ assignment, userProfile, isOpen, onToggle, bubblePos, setScheduleData, setUnscheduledAssignments, setPlaygroundAssignments }) => {
    const quoteId = assignment?.quote_info?.id;
    const notes = assignment?.quote_info?.quick_notes || [];
    const hasNotes = notes.length > 0;

    const [newNoteText, setNewNoteText] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editingText, setEditingText] = useState('');
    const [saving, setSaving] = useState(false);
    const btnRef = useRef(null);
    const bubbleRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e) => {
            if (bubbleRef.current && !bubbleRef.current.contains(e.target) &&
                btnRef.current && !btnRef.current.contains(e.target)) {
                onToggle(false, null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const openBubble = (e) => {
        e.stopPropagation();
        if (isOpen) { onToggle(false, null); return; }
        if (!btnRef.current) return;
        const rect = btnRef.current.getBoundingClientRect();
        const bubbleWidth = 320;
        const bubbleHeight = 400;
        const flipLeft = rect.left + bubbleWidth > window.innerWidth - 12;
        const spaceBelow = window.innerHeight - rect.bottom;
        const showAbove = spaceBelow < bubbleHeight && rect.top > bubbleHeight;
        const pos = { x: flipLeft ? rect.right : rect.left, y: showAbove ? rect.top - 8 : rect.bottom + 8, showAbove, flipLeft };
        onToggle(true, pos);
    };

    const saveNotes = async (updated) => {
        if (!quoteId) return;
        setSaving(true);
        try {
            await updateQuoteQuickNotes(quoteId, updated);
            const patchItem = (item) => {
                if (item.quote_info?.id !== quoteId) return item;
                return { ...item, quote_info: { ...item.quote_info, quick_notes: updated } };
            };
            setScheduleData(prev => {
                const next = {};
                for (const dk in prev) next[dk] = prev[dk].map(patchItem);
                return next;
            });
            setUnscheduledAssignments(prev => prev.map(patchItem));
            setPlaygroundAssignments(prev => prev.map(patchItem));
        } catch (err) {
            console.error('Failed to save quick notes:', err);
            Toast.error('Failed to save note');
        } finally {
            setSaving(false);
        }
    };

    const handleAddNote = async () => {
        const text = newNoteText.trim();
        if (!text) return;
        const newNote = {
            id: crypto.randomUUID(),
            note: text,
            type: "site-measure" || null,
            added_by_id: userProfile?.id || null,
            added_by_name: userProfile?.full_name || userProfile?.username || userProfile?.email || 'Unknown',
            added_at: new Date().toISOString(),
            edited_at: null,
        };
        setNewNoteText('');
        await saveNotes([...notes, newNote]);
    };

    const handleEditSave = async (noteId) => {
        const text = editingText.trim();
        if (!text) return;
        const updated = notes.map(n => n.id === noteId ? { ...n, note: text, edited_at: new Date().toISOString() } : n);
        setEditingId(null);
        await saveNotes(updated);
    };

    const handleDelete = async (noteId) => {
        await saveNotes(notes.filter(n => n.id !== noteId));
    };

    const bubbleJsx = isOpen ? (
        <div
            ref={bubbleRef}
            onPointerDown={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            onTouchStart={e => e.stopPropagation()}
            className="fixed z-[9990] w-80 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden"
            style={{
                [bubblePos.flipLeft ? 'right' : 'left']: bubblePos.flipLeft ? window.innerWidth - bubblePos.x : bubblePos.x,
                [bubblePos.showAbove ? 'bottom' : 'top']: bubblePos.showAbove ? window.innerHeight - bubblePos.y : bubblePos.y,
            }}
            onClick={e => e.stopPropagation()}
        >
            <div className="px-3 pt-3 pb-2">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Job Info</span>
                    <button type="button" onClick={() => onToggle(false, null)} className="text-gray-400 hover:text-gray-600">
                        <FiX className="w-3.5 h-3.5" />
                    </button>
                </div>
                <div className="space-y-1">
                    <div className="flex items-center gap-2">
                        <FiHash className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="text-xs font-semibold text-gray-800">{assignment.unique_id}</span>
                        {assignment.quote_info?.child_unique_id && (
                            <span className="text-xs font-mono bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                                {assignment.quote_info.child_unique_id}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <FiUser className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="text-xs text-gray-700 font-medium">{assignment.customer_name}</span>
                    </div>
                    {assignment.customer_address && (
                        <div className="flex items-start gap-2">
                            <FiMapPin className="w-3 h-3 text-gray-400 flex-shrink-0 mt-0.5" />
                            <span className="text-xs text-gray-600">{assignment.customer_address}</span>
                        </div>
                    )}
                </div>
            </div>
            <div className="border-t border-gray-100" />
            <div className="px-3 pt-2 pb-3 max-h-64 overflow-y-auto">
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Quick Notes</span>
                {notes.length === 0 && <p className="text-xs text-gray-400 italic mt-1 mb-2">No notes yet</p>}
                <div className="space-y-2 mt-1">
                    {notes.map(n => (
                        <div key={n.id} className="bg-gray-50 rounded-lg p-2 text-xs">
                            {editingId === n.id ? (
                                <div className="space-y-1">
                                    <textarea
                                        autoFocus
                                        className="w-full text-xs border border-blue-300 rounded p-1 resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                                        rows={2}
                                        value={editingText}
                                        onChange={e => setEditingText(e.target.value)}
                                    />
                                    <div className="flex gap-1">
                                        <button type="button" onClick={() => handleEditSave(n.id)} disabled={saving}
                                            className="px-2 py-0.5 bg-blue-600 text-white rounded text-[10px] font-medium disabled:opacity-50">Save</button>
                                        <button type="button" onClick={() => setEditingId(null)}
                                            className="px-2 py-0.5 text-gray-500 hover:text-gray-700 text-[10px]">Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-start justify-between gap-1">
                                        <p className="text-gray-800 flex-1 leading-snug">{n.note}</p>
                                        <div className="flex gap-1 flex-shrink-0">
                                            <button type="button" onClick={() => { setEditingId(n.id); setEditingText(n.note); }}
                                                className="text-gray-400 hover:text-blue-600 p-0.5">
                                                <FiEdit2 className="w-3 h-3" />
                                            </button>
                                            <button type="button" onClick={() => handleDelete(n.id)} disabled={saving}
                                                className="text-gray-400 hover:text-red-500 p-0.5 disabled:opacity-50">
                                                <FiTrash2 className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="text-[10px] text-gray-400 mt-1">
                                        {n.added_by_name} · {formatNoteDate(n.edited_at || n.added_at)}
                                        {n.edited_at && <span className="ml-1 italic">(edited)</span>}
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>
                <div className="mt-2 space-y-1">
                    <textarea
                        className="w-full text-xs border border-gray-200 rounded-lg p-2 resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                        rows={2}
                        placeholder="Add a quick note…"
                        value={newNoteText}
                        onChange={e => setNewNoteText(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddNote(); } }}
                    />
                    <button type="button" onClick={handleAddNote} disabled={!newNoteText.trim() || saving}
                        className="flex items-center gap-1 text-[11px] px-2 py-1 bg-blue-600 text-white rounded-lg disabled:opacity-40 hover:bg-blue-700 font-medium">
                        <FiPlus className="w-3 h-3" /> Add Note
                    </button>
                </div>
            </div>
        </div>
    ) : null;

    return (
        <>
            <button
                ref={btnRef}
                aria-label="Quick notes"
                type="button"
                aria-label="Site measure notes"
                className={`site-measure-note-button p-1 rounded-md border transition-all ${hasNotes
                    ? 'bg-blue-500 text-white border-blue-600 hover:bg-blue-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'}`}
                onClick={openBubble}
            >
                <FiMessageSquare className="w-3 h-3" />
            </button>
            {createPortal(bubbleJsx, document.body)}
        </>
    );
};

// ─── SortableCard (module-level memo for stable identity across renders) ──────
const SortableCard = memo(function SortableCard({ job,
    userProfile,
    isDisabled,
    userRole,
    companySlug,
    navigate,
    onConfirmToggle,
    openNotesBubble,
    setOpenNotesBubble,
    setScheduleData,
    setUnscheduledJobs,
    isMobile,
    onMobileMove
}) {
    const location = useLocation();

    const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
        id: job.schedule_row_uuid,
        data: { job },
        disabled: isDisabled || isMobile,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition: null,
        opacity: isDragging ? 0 : 1,
        contain: 'layout style paint',
        willChange: isDragging ? 'transform' : undefined,
    };

    const isAdmin = userRole === 'admin' || userRole === 'super_admin';
    // Each card represents a specific quote type — use its colour from QUOTE_CONFIG
    const smCfg = QUOTE_CONFIG[job.sm_type] || { label: job.sm_type || '?', bg: 'bg-gray-100', text: 'text-gray-700' };
    const refQuote = job.quote_info;
    const isConfirmed = !!refQuote?.site_measure_confirmed;

    const DEFAULT_NOTES_POS = { x: 0, y: 0, showAbove: false, flipLeft: false };

    const notesOpen = openNotesBubble?.quoteId === job?.quote_info?.id;
    const notesBubblePos = notesOpen ? openNotesBubble : DEFAULT_NOTES_POS;

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...(isMobile ? {} : attributes)}
            {...(notesOpen ? {} : listeners)}
            className={`site-measure-card relative border-2 rounded-lg p-3 pb-7 shadow-sm hover:shadow-md transition-shadow
                ${isAdmin ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}
                ${isConfirmed
                    ? 'bg-green-50 border-green-300'
                    : job.urgent_quote ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}
            onClick={() =>
                navigate(`/${companySlug}/assignment/${job.id}`, { state: { from: location.pathname } })
            }
        >
            {/* Header: Job ID + Urgency + Site Measure type pill */}
            <div className="flex items-start justify-between gap-2 mb-1.5">

                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    <FiHash className="w-3 h-3 text-gray-700 flex-shrink-0" />
                    <TextTooltip text={job.schedule_row_id}>
                        <span className="font-semibold text-white text-sm bg-gray-500 rounded px-1 truncate">
                            {job.schedule_row_id}
                        </span>
                    </TextTooltip>
                    {job.urgent_quote && (
                        <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-red-100 text-red-700 flex-shrink-0">
                            URGENT
                        </span>
                    )}
                </div>
                {/* Type badge for this specific card */}
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase leading-none flex-shrink-0 ${smCfg.bg} ${smCfg.text}`}>
                    {smCfg.label}
                </span>
            </div>

            {/* Customer name */}
            <div className="flex items-center gap-1.5 mb-1">
                <FiUser className="w-3 h-3 text-blue-500 flex-shrink-0" />
                <span className="text-sm text-gray-700 truncate font-medium">{job.customer_name}</span>
            </div>

            {/* Address */}
            {job.customer_address ? (
                <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.customer_address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    className="text-xs text-green-700 hover:text-green-800 flex items-center gap-1 mb-2 truncate block"
                >
                    <MapIcon className="w-3 h-3 flex-shrink-0" />
                    {job.customer_address}
                </a>
            ) : (
                <div className="site-measure-missing-address hidden mb-2 text-xs text-gray-500 items-center gap-1"><MapIcon className="w-3 h-3 flex-shrink-0" />No address provided</div>
            )}

            <MobileJobActions job={job}
                className="absolute bottom-1.5 right-1.5 flex flex-row items-center gap-1"
                onClick={e => e.stopPropagation()}
                onPointerDown={e => e.stopPropagation()}
            >
                <button
                    type="button"
                    onClick={e => {
                        e.stopPropagation();
                        onConfirmToggle?.(job, !isConfirmed);
                    }}
                    title={isConfirmed ? 'Mark as unconfirmed' : 'Mark as confirmed'}
                    className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border transition-colors
            ${isConfirmed
                            ? 'bg-green-500 border-green-600 text-white hover:bg-green-600'
                            : 'bg-white border-gray-300 text-gray-400 hover:border-green-400 hover:text-green-500'}`}
                >
                    <svg
                        className={`w-3 h-3 flex-shrink-0 ${isConfirmed ? 'text-white' : 'text-gray-400'}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={3}
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {isConfirmed ? 'Completed' : 'Complete'}
                </button>

                {isMobile && isAdmin && <button type="button" onClick={e => { e.stopPropagation(); onMobileMove(job); }} aria-label="Move job" title="Move job" className="p-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 transition-all shadow-md"><FiMove className="w-3.5 h-3.5" /></button>}
                <QuickNotesButton aria-label="Quick notes"
                    assignment={job}
                    userProfile={userProfile}
                    isOpen={notesOpen}
                    onToggle={(val, pos) =>
                        setOpenNotesBubble(
                            val ? { quoteId: job?.quote_info?.id, ...pos } : null
                        )
                    }
                    bubblePos={notesBubblePos}
                    setScheduleData={setScheduleData}
                    setUnscheduledAssignments={setUnscheduledJobs}
                    setPlaygroundAssignments={() => { }}
                />


            </MobileJobActions>
        </div>
    );
});

// ─── VirtualDroppableDay (module-level memo) ──────────────────────────────────
// Combines dnd-kit useDroppable with @tanstack/react-virtual for performant rendering.
const VirtualDroppableDay = memo(function VirtualDroppableDay({
    dayKey, dayLabel, dayDate, dayIsPast, dayIsToday, dayHolidays,
    jobs, sortedIds, jobCount, isFiltered, renderCard,
}) {
    const { isOver, setNodeRef: setDropRef } = useDroppable({ id: `day-${dayKey}` });
    const scrollRef = useRef(null);
    const isHoliday = dayHolidays.length > 0;

    const setRefs = useCallback((node) => {
        setDropRef(node);
        scrollRef.current = node;
    }, [setDropRef]);

    const virtualizer = useVirtualizer({
        count: jobs.length,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => 180,
        overscan: 3,
    });

    return (
        <div
            ref={setRefs}
            className={`site-measure-day ${jobs.length === 0 ? 'is-empty' : ''} border-2 rounded-xl overflow-y-auto h-[70vh] transition-colors
                ${isOver ? 'border-blue-300 bg-blue-50' :
                    isHoliday ? 'border-amber-400 bg-gray-50' :
                        dayIsPast ? 'border-gray-300 bg-gray-100/50' :
                            'border-gray-200 bg-gray-50'}
                hover:border-blue-300 ${dayIsPast ? 'opacity-80' : ''}`}
        >
            {/* Sticky header */}
            <div
                className={`sticky top-0 z-10 px-3 py-1 border-b
                    ${isHoliday ? 'bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200' :
                        dayIsPast ? 'bg-gradient-to-r from-gray-100 to-gray-200 border-gray-200' :
                            'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200'}`}
            >
                <div className="p-1 flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-2">
                            <h3 className={`font-bold text-lg ${dayIsToday ? 'text-teal-700' : 'text-gray-800'}`}>
                                {dayLabel}
                            </h3>
                            <p className="text-xs text-gray-500 font-medium">{dayDate}</p>
                        </div>
                        {isHoliday && (
                            <p className="text-xs text-amber-700 font-semibold flex items-center gap-1">
                                <PartyPopper className="w-3 h-3" />
                                {dayHolidays[0]}{dayHolidays.length > 1 ? ` +${dayHolidays.length - 1}` : ''}
                            </p>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        {dayIsToday && (
                            <span className="bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md animate-pulse">
                                TODAY
                            </span>
                        )}
                        {dayIsPast && !dayIsToday && (
                            <span className="bg-gray-400 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md">
                                PAST
                            </span>
                        )}
                        <div
                            className={`h-5 px-2 min-w-[20px] flex items-center justify-center rounded-full text-[10px] font-bold shadow-md
                                ${jobCount > 0 ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'}`}
                        >
                            {jobCount}
                        </div>
                    </div>
                </div>
            </div>

            {/* Virtualised card list */}
            <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
                {jobs.length > 0 ? (
                    <div
                        className="px-2 pt-2 relative"
                        style={{ height: `${virtualizer.getTotalSize()}px` }}
                    >
                        {virtualizer.getVirtualItems().map(vi => (
                            <div
                                key={vi.key}
                                data-index={vi.index}
                                ref={virtualizer.measureElement}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    transform: `translateY(${vi.start}px)`,
                                    paddingLeft: 8,
                                    paddingRight: 8,
                                    paddingBottom: 8,
                                }}
                            >
                                {renderCard(jobs[vi.index])}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <FiCalendar className="w-6 h-6 text-gray-300 mx-auto mb-2" />
                        <p className="text-xs text-gray-500">
                            {isFiltered ? 'No matches' : 'No site measures scheduled'}
                        </p>
                    </div>
                )}
            </SortableContext>
        </div>
    );
});

// ─── VirtualUnscheduledGrid ───────────────────────────────────────────────────
const UNSCHED_COLS = 5;
const VirtualUnscheduledGrid = ({ jobs, sortedIds, renderCard, isFiltered }) => {
    const scrollRef = useRef(null);
    const rowCount = Math.ceil(jobs.length / UNSCHED_COLS);

    const virtualizer = useVirtualizer({
        count: rowCount,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => 200,
        overscan: 2,
    });

    if (jobs.length === 0) {
        return (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <FiPackage className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">
                    {isFiltered ? 'No matching unscheduled site measures' : 'All site measures have been scheduled'}
                </p>
                <p className="text-sm text-gray-400 mt-1">
                    {isFiltered ? 'Try a different search term' : 'Great job!'}
                </p>
            </div>
        );
    }

    return (
        <SortableContext items={sortedIds} strategy={rectSortingStrategy}>
            <div ref={scrollRef} className="overflow-y-auto" style={{ maxHeight: '520px' }}>
                <div className="relative" style={{ height: `${virtualizer.getTotalSize()}px` }}>
                    {virtualizer.getVirtualItems().map(vRow => {
                        const startIdx = vRow.index * UNSCHED_COLS;
                        const rowItems = jobs.slice(startIdx, startIdx + UNSCHED_COLS);
                        return (
                            <div
                                key={vRow.key}
                                data-index={vRow.index}
                                ref={virtualizer.measureElement}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    transform: `translateY(${vRow.start}px)`,
                                }}
                                className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 pb-4"
                            >
                                {rowItems.map(j => renderCard(j))}
                            </div>
                        );
                    })}
                </div>
            </div>
        </SortableContext>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────
const SiteMeasureSchedule = () => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();
    const { companySlug } = useParams();
    const isMobile = useMobileSchedule();
    const holidayMap = useHolidays(company?.state);

    // ── Week offset ───────────────────────────────────────────────────────────
    const [weekOffset, setWeekOffset] = useState(() => {
        try { const v = localStorage.getItem('siteMeasure_weekOffset'); return v !== null ? JSON.parse(v) : 0; } catch { return 0; }
    });
    const weekOffsetRef = useRef(0);
    useEffect(() => { weekOffsetRef.current = weekOffset; }, [weekOffset]);
    useEffect(() => {
        try { localStorage.setItem('siteMeasure_weekOffset', JSON.stringify(weekOffset)); } catch { }
    }, [weekOffset]);

    // ── Data ──────────────────────────────────────────────────────────────────
    const [scheduleData, setScheduleData] = useState({});
    const [unscheduledJobs, setUnscheduledJobs] = useState([]);
    const scheduleDataRef = useRef({});
    const unscheduledJobsRef = useRef([]);
    const isDraggingRef = useRef(false);
    // Tracks job IDs of moves we initiated — suppresses our own subscription echo
    const selfMovedRef = useRef(new Set());
    // Always-current snapshot for realtime handlers
    const latestRef = useRef({});

    // ── DnD state ─────────────────────────────────────────────────────────────
    const [activeId, setActiveId] = useState(null);
    const [draggedItemCache, setDraggedItemCache] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [mobileMoveJob, setMobileMoveJob] = useState(null);
    const [pendingMove, setPendingMove] = useState(null);
    const [pastDateWarning, setPastDateWarning] = useState(false);

    // ── Confirm-checkbox state ────────────────────────────────────────────────
    const [pendingConfirm, setPendingConfirm] = useState(null); // { job, newValue }
    const [showConfirmCheck, setShowConfirmCheck] = useState(false);
    const [confirmCheckLoading, setConfirmCheckLoading] = useState(false);

    // ── UI state ──────────────────────────────────────────────────────────────
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState(() => {
        try { return localStorage.getItem('siteMeasure_searchQuery') ?? ''; } catch { return ''; }
    });
    useEffect(() => {
        try { localStorage.setItem('siteMeasure_searchQuery', searchQuery); } catch { }
    }, [searchQuery]);
    const [isScrolled, setIsScrolled] = useState(false);
    const [showSaturday, setShowSaturday] = useState(true);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [datePickerValue, setDatePickerValue] = useState(null);

    const [openNotesBubble, setOpenNotesBubble] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
    );
    const dropAnimationConfig = {
        duration: 200,
        easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
        sideEffects: defaultDropAnimationSideEffects({ styles: { active: { opacity: '0.5' } } }),
    };

    // Keep refs in sync
    useEffect(() => { scheduleDataRef.current = scheduleData; }, [scheduleData]);
    useEffect(() => { unscheduledJobsRef.current = unscheduledJobs; }, [unscheduledJobs]);
    latestRef.current = { scheduleData, unscheduledJobs, weekOffset };

    useEffect(() => {
        const h = () => setIsScrolled(window.scrollY > 200);
        window.addEventListener('scroll', h, { passive: true });
        return () => window.removeEventListener('scroll', h);
    }, []);

    // ── Date helpers ──────────────────────────────────────────────────────────
    const getAustralianDate = () =>
        new Date(new Date().toLocaleString('en-US', { timeZone: 'Australia/Melbourne' }));

    const getCurrentWeekStart = () =>
        addWeeks(startOfWeek(getAustralianDate(), { weekStartsOn: 1 }), weekOffset);

    const getWeekLabel = () => {
        const ws = getCurrentWeekStart();
        return `${format(ws, 'dd MMM')} – ${format(endOfWeek(ws, { weekStartsOn: 1 }), 'dd MMM yyyy')}`;
    };

    const getMonthLabel = () => format(getCurrentWeekStart(), 'MMMM yyyy');

    const getDayDate = (i) => format(addDays(getCurrentWeekStart(), i), 'dd/MM/yyyy');

    const getDayDateObject = (i) => {
        const d = addDays(getCurrentWeekStart(), i);
        return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0));
    };

    const toDateKey = (date) => {
        const d = new Date(date);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const getHolidaysForDate = (date) => holidayMap.get(toDateKey(date)) || [];
    const isDayToday = (i) => isToday(addDays(getCurrentWeekStart(), i));
    const isDateInPast = (date) => isBefore(startOfDay(date), startOfDay(getAustralianDate()));

    const days = [
        { label: 'Mon', key: 'Monday' },
        { label: 'Tue', key: 'Tuesday' },
        { label: 'Wed', key: 'Wednesday' },
        { label: 'Thu', key: 'Thursday' },
        { label: 'Fri', key: 'Friday' },
        ...(showSaturday ? [{ label: 'Sat', key: 'Saturday' }] : []),
    ];

    // ── Fetch ref (stable for subscription handlers) ───────────────────────────
    const fetchRef = useRef(null);

    const fetchAssignments = async () => {
        if (!company?.id) return;
        setIsLoading(true);
        try {
            const weekStart = getCurrentWeekStart();
            const [scheduledRes, unscheduledRes] = await Promise.all([
                getSiteMeasureScheduled(company.id, { weekStart }),
                getSiteMeasureUnscheduled(company.id),
            ]);
            const sd = scheduledRes.data || {};
            const ud = unscheduledRes.data || [];
            scheduleDataRef.current = sd;
            unscheduledJobsRef.current = ud;
            setScheduleData(sd);
            setUnscheduledJobs(ud);
        } catch (err) {
            console.error('Error fetching site measure assignments:', err);
            Toast.error('Failed to load site measure schedule');
        } finally {
            setIsLoading(false);
        }
    };
    fetchRef.current = fetchAssignments;

    // ── Initial fetch + refetch on week change ────────────────────────────────
    useEffect(() => {
        if (company?.id) fetchRef.current?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company?.id, weekOffset]);


    const insertSiteMeasureCardLocally = useCallback((newRow) => {
        if (!newRow?.site_measure_required) return;

        const getTargetDayKey = (dateStr) => {
            if (!dateStr) return null;

            const d = new Date(dateStr);
            const weekStart = addWeeks(
                startOfWeek(getAustralianDate(), { weekStartsOn: 1 }),
                weekOffsetRef.current
            );
            const diffDays = Math.floor((d - weekStart) / (1000 * 60 * 60 * 24));

            if (diffDays >= 0 && diffDays <= (showSaturday ? 5 : 4)) {
                return d.toLocaleDateString('en-US', { weekday: 'long' });
            }

            return null;
        };

        const targetDayKey = getTargetDayKey(newRow.site_measure_date);

        const card = {
            ...newRow,
            schedule_row_uuid: newRow.schedule_row_uuid || `${newRow.id}-${newRow.sm_type || 'sm'}`,
            schedule_row_id: newRow.schedule_row_id || newRow.unique_id,
            quote_info: newRow.quote_info || null,
            job_quotes: newRow.job_quotes || [],
        };

        setScheduleData(prev => {
            const next = {};
            for (const day of Object.keys(prev)) {
                next[day] = prev[day].filter(j => j.schedule_row_uuid !== card.schedule_row_uuid);
            }

            if (targetDayKey) {
                next[targetDayKey] = [...(next[targetDayKey] || []), card];
            }

            return next;
        });

        setUnscheduledJobs(prev => {
            const cleaned = prev.filter(j => j.schedule_row_uuid !== card.schedule_row_uuid);

            if (!targetDayKey) {
                return [...cleaned, card];
            }

            return cleaned;
        });
    }, [showSaturday]);

    // ── Realtime subscriptions ────────────────────────────────────────────────
    useEffect(() => {
        if (!company?.id) return;

        const patchJob = (jobId, updater) => {
            setScheduleData(prev => {
                const next = {};
                for (const day of Object.keys(prev)) {
                    next[day] = prev[day].map(j => j.id === jobId ? updater(j) : j);
                }
                return next;
            });
            setUnscheduledJobs(prev => prev.map(j => j.id === jobId ? updater(j) : j));
        };

        const removeJob = (jobId) => {
            setScheduleData(prev => {
                const next = {};
                for (const day of Object.keys(prev)) next[day] = prev[day].filter(j => j.id !== jobId);
                return next;
            });
            setUnscheduledJobs(prev => prev.filter(j => j.id !== jobId));
        };

        const handleJobUpdate = (payload) => {
            if (isDraggingRef.current) return;

            if (payload.eventType === 'DELETE') {
                removeJob(payload.old.id);
                return;
            }
            if (payload.eventType === 'INSERT') {
                // if (payload.new?.site_measure_required) fetchRef.current?.();
                if (payload.new?.site_measure_required) {
                    insertSiteMeasureCardLocally(payload.new);
                }
                return;
            }
            if (payload.eventType === 'UPDATE') {
                const j = payload.new;

                if (j.is_cancelled === true) {
                    removeJob(j.id);
                    return;
                }

                if (payload.old?.is_cancelled === true && j.is_cancelled !== true) {
                    fetchRef.current?.();
                    return;
                }

                if (!j.site_measure_required) {
                    // No longer a site measure job — remove from all views
                    removeJob(j.id);
                } else {
                    // Patch job-level fields in place
                    patchJob(j.id, a => ({
                        ...a,
                        unique_id: j.unique_id,
                        customer_name: j.customer_name,
                        customer_address: j.customer_address,
                        main_status: j.main_status,
                        on_hold: j.on_hold,
                        urgent_quote: j.urgent_quote,
                        urgent_design: j.urgent_design,
                        site_measure_wall: j.site_measure_wall,
                        site_measure_floor: j.site_measure_floor,
                        site_measure_truss: j.site_measure_truss,
                    }));
                }
            }
        };

        const handleQuotesUpdate = (payload) => {
            if (isDraggingRef.current) return;
            if (payload.eventType === 'DELETE' || payload.eventType === 'INSERT') return;

            const updated = payload.new;
            const jobId = updated?.job_id;
            if (!jobId) return;

            // Skip echoes from our own drag moves (tracked by quote ID)
            if (selfMovedRef.current.has(updated.id)) return;

            // A job_quotes change may affect site_measure_date or design_status;
            // patch quote_info in place for design_status, refetch for date changes.
            const hasDateField = 'site_measure_date' in updated;
            if (hasDateField) {
                const updatedDate = updated.site_measure_date;

                const getTargetDayKey = (dateValue) => {
                    if (!dateValue) return null;

                    const d = new Date(dateValue);
                    const weekStart = addWeeks(
                        startOfWeek(getAustralianDate(), { weekStartsOn: 1 }),
                        weekOffsetRef.current
                    );

                    const diffDays = Math.floor((d - weekStart) / (1000 * 60 * 60 * 24));
                    const maxDayIndex = showSaturday ? 5 : 4;

                    if (diffDays >= 0 && diffDays <= maxDayIndex) {
                        return d.toLocaleDateString('en-US', { weekday: 'long' });
                    }

                    return null;
                };

                const targetDayKey = getTargetDayKey(updatedDate);

                const patchJobWithQuote = (job) => {
                    const nextQuoteInfo =
                        job.quote_info?.id === updated.id
                            ? { ...job.quote_info, ...updated }
                            : job.quote_info;

                    return {
                        ...job,
                        site_measure_date: updatedDate,
                        quote_info: nextQuoteInfo,
                        job_quotes: (job.job_quotes || []).map(q =>
                            q.id === updated.id ? { ...q, ...updated } : q
                        ),
                    };
                };

                const allCurrentJobs = [
                    ...Object.values(scheduleDataRef.current).flat(),
                    ...unscheduledJobsRef.current,
                ];

                const existingJob = allCurrentJobs.find(j => j.quote_info?.id === updated.id);

                if (!existingJob) {
                    return;
                }

                const movedJob = patchJobWithQuote(existingJob);

                setScheduleData(prev => {
                    const next = {};

                    for (const day of Object.keys(prev)) {
                        next[day] = prev[day].filter(j => j.quote_info?.id !== updated.id);
                    }

                    if (targetDayKey) {
                        next[targetDayKey] = [...(next[targetDayKey] || []), movedJob];
                    }

                    return next;
                });

                setUnscheduledJobs(prev => {
                    const cleaned = prev.filter(j => j.quote_info?.id !== updated.id);

                    if (!targetDayKey) {
                        return [...cleaned, movedJob];
                    }

                    return cleaned;
                });

                return;
            } else {
                // Design/production/invoice status changed → patch in place
                patchJob(jobId, a => ({
                    ...a,
                    job_quotes: (a.job_quotes || []).map(q =>
                        q.id === updated.id ? { ...q, ...updated } : q
                    ),
                    // If quote_info is this quote, patch it too
                    quote_info: a.quote_info?.id === updated.id
                        ? { ...a.quote_info, ...updated }
                        : a.quote_info,
                }));
            }
        };

        const jobSub = subscribeToJobUpdates(company.id, handleJobUpdate);
        const quotesSub = subscribeToJobQuotesUpdates(company.id, handleQuotesUpdate);

        return () => {
            unsubscribeFromJobUpdates(jobSub);
            unsubscribeFromJobUpdates(quotesSub);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company?.id]);

    // ── Search ────────────────────────────────────────────────────────────────
    const useDebounce = (val, delay) => {
        const [dv, setDv] = useState(val);
        useEffect(() => {
            const h = setTimeout(() => setDv(val), delay);
            return () => clearTimeout(h);
        }, [val, delay]);
        return dv;
    };
    const debouncedSearch = useDebounce(searchQuery, 300);

    const matchesSearch = (job) => {
        if (!debouncedSearch.trim()) return true;
        const q = debouncedSearch.toLowerCase();
        return (
            job.schedule_row_id?.toLowerCase().includes(q) ||
            job.unique_id?.toLowerCase().includes(q) ||
            job.customer_name?.toLowerCase().includes(q) ||
            job.customer_address?.toLowerCase().includes(q) ||
            job.carpenter_supervisor_name?.toLowerCase().includes(q)
        );
    };

    // ── Drag & Drop handlers ──────────────────────────────────────────────────
    const handleDragStart = (event) => {
        isDraggingRef.current = true;
        setActiveId(event.active.id);
        const job = event.active.data.current?.job;
        if (job) setDraggedItemCache(job);
    };

    const handleDragEnd = (event) => {
        isDraggingRef.current = false;
        const { active, over } = event;
        const job = draggedItemCache || active.data.current?.job;
        setDraggedItemCache(null);
        setActiveId(null);

        if (!over || !job) return;

        // ── Unscheduled zone or drop on an unscheduled card ───────────────────
        const isOverUnscheduled =
            over.id === 'unscheduled-zone' ||
            unscheduledJobs.some(j => j.schedule_row_uuid === over.id);

        if (isOverUnscheduled) {
            // Already unscheduled → no-op
            if (unscheduledJobs.some(j => j.schedule_row_uuid === job.schedule_row_uuid)) return;

            // Optimistic: remove this specific card from its day column
            setScheduleData(prev => {
                const next = {};
                for (const dk in prev) next[dk] = prev[dk].filter(j => j.schedule_row_uuid !== job.schedule_row_uuid);
                return next;
            });

            setPendingMove({ job, removeDate: true, isPastDate: false });
            setPastDateWarning(false);
            setShowConfirmation(true);
            return;
        }

        // ── Day column drop ───────────────────────────────────────────────────
        let targetDayKey = null;
        if (typeof over.id === 'string' && over.id.startsWith('day-')) {
            targetDayKey = over.id.slice(4); // 'day-Monday' → 'Monday'
        } else {
            for (const [day, jobs] of Object.entries(scheduleData)) {
                if (jobs.some(j => j.schedule_row_uuid === over.id)) { targetDayKey = day; break; }
            }
        }

        if (!targetDayKey) return;

        // Find source day
        let sourceDayKey = null;
        for (const [day, jobs] of Object.entries(scheduleData)) {
            if (jobs.some(j => j.schedule_row_uuid === job.schedule_row_uuid)) { sourceDayKey = day; break; }
        }

        // Same-day drop → no-op (reordering not tracked for site measure)
        if (sourceDayKey && sourceDayKey === targetDayKey) return;

        const dayIndex = days.findIndex(d => d.key === targetDayKey);
        if (dayIndex === -1) return;

        const targetDate = getDayDateObject(dayIndex);
        const isPast = isDateInPast(targetDate);

        setPendingMove({ job, newDate: targetDate, dayKey: targetDayKey, isPastDate: isPast });
        setPastDateWarning(isPast);
        setShowConfirmation(true);
    };

    // const handleConfirmMove = async () => {
    //     if (!pendingMove) return;
    //     setIsLoading(true);
    //     try {
    //         const { job, newDate, removeDate } = pendingMove;

    //         // Mark as self-moved to suppress subscription echo (tracked by quote ID)
    //         const quoteId = job.quote_info?.id;
    //         selfMovedRef.current.add(quoteId);
    //         setTimeout(() => selfMovedRef.current.delete(quoteId), 5000);

    //         const { error } = await updateSiteMeasureDate(quoteId, removeDate ? null : newDate);
    //         if (error) throw error;

    //         // ── Activity log ────────────────────────────────────────────────
    //         {
    //             const displayId = job.schedule_row_id || job.unique_id || '';
    //             const smLabel = job.sm_type ? ` (${job.sm_type})` : '';
    //             const details = removeDate
    //                 ? `Removed site measure date for #${displayId}${smLabel}`
    //                 : `Scheduled site measure #${displayId}${smLabel} to ${pendingMove.dayKey} (${format(newDate, 'd MMM')})`;
    //             createJobActionLog({
    //                 job_id: job.id,
    //                 user_id: userProfile?.id,
    //                 user_name: userProfile?.full_name || userProfile?.username || userProfile?.email || 'Unknown',
    //                 user_role: userProfile?.role,
    //                 action: removeDate ? 'unschedule' : 'reschedule',
    //                 details,
    //                 quote_type: job.sm_type || null,
    //                 action_origin: 'schedule-sitemeasure',
    //                 company_id: company.id
    //             }).catch(() => { });
    //         }

    //         await fetchRef.current?.();
    //     } catch (err) {
    //         console.error('Failed to update site measure date:', err);
    //         Toast.error('Failed to update site measure date');
    //         await fetchRef.current?.();
    //     } finally {
    //         setIsLoading(false);
    //         setShowConfirmation(false);
    //         setPendingMove(null);
    //         setActiveId(null);
    //         setPastDateWarning(false);
    //     }
    // };

    const handleConfirmMove = async () => {
        if (!pendingMove) return;

        setIsLoading(true);

        try {
            const { job, newDate, removeDate, dayKey } = pendingMove;
            const quoteId = job.quote_info?.id;

            // Mark as self-moved so realtime echo does not patch it again immediately
            selfMovedRef.current.add(quoteId);
            setTimeout(() => selfMovedRef.current.delete(quoteId), 5000);

            const { error } = await updateSiteMeasureDate(quoteId, removeDate ? null : newDate);
            if (error) throw error;

            const updatedDate = removeDate ? null : newDate;

            const patchMovedJob = (item) => {
                if (item.schedule_row_uuid !== job.schedule_row_uuid) return item;

                return {
                    ...item,
                    site_measure_date: updatedDate,
                    quote_info: item.quote_info
                        ? {
                            ...item.quote_info,
                            site_measure_date: updatedDate,
                        }
                        : item.quote_info,
                    job_quotes: (item.job_quotes || []).map(q =>
                        q.id === quoteId
                            ? { ...q, site_measure_date: updatedDate }
                            : q
                    ),
                };
            };

            // Remove this card from all day columns first
            setScheduleData(prev => {
                const next = {};

                for (const dk of Object.keys(prev)) {
                    next[dk] = prev[dk].filter(j => j.schedule_row_uuid !== job.schedule_row_uuid);
                }

                // If rescheduling to a visible day, add it there
                if (!removeDate && dayKey && days.some((day, index) => day.key === dayKey && getDayDateObject(index).toISOString().slice(0, 10) === newDate.toISOString().slice(0, 10))) {
                    const movedJob = patchMovedJob(job);
                    next[dayKey] = [...(next[dayKey] || []), movedJob];
                }

                return next;
            });

            // Update unscheduled list
            setUnscheduledJobs(prev => {
                const cleaned = prev.filter(j => j.schedule_row_uuid !== job.schedule_row_uuid);

                // If removing date, move into unscheduled
                if (removeDate) {
                    return [...cleaned, patchMovedJob(job)];
                }

                // If rescheduling to a day, make sure it's removed from unscheduled
                return cleaned;
            });

            // ── Activity log ────────────────────────────────────────────────
            {
                const displayId = job.schedule_row_id || job.unique_id || '';
                const smLabel = job.sm_type ? ` (${job.sm_type})` : '';
                const details = removeDate
                    ? `Removed site measure date for #${displayId}${smLabel}`
                    : `Scheduled site measure #${displayId}${smLabel} to ${dayKey} (${format(newDate, 'd MMM')})`;

                createJobActionLog({
                    job_id: job.id,
                    user_id: userProfile?.id,
                    user_name: userProfile?.full_name || userProfile?.username || userProfile?.email || 'Unknown',
                    user_role: userProfile?.role,
                    action: removeDate ? 'unschedule' : 'reschedule',
                    details,
                    quote_type: job.sm_type || null,
                    action_origin: 'schedule-sitemeasure',
                    company_id: company.id
                }).catch(() => { });
            }

            setShowConfirmation(false);
            setPendingMove(null);
            setActiveId(null);
            setPastDateWarning(false);
        } catch (err) {
            console.error('Failed to update site measure date:', err);
            Toast.error('Failed to update site measure date');

            // Only refetch on failure to recover state
            await fetchRef.current?.();

            setShowConfirmation(false);
            setPendingMove(null);
            setActiveId(null);
            setPastDateWarning(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelMove = () => {
        // Restore card if we optimistically removed it
        if (pendingMove?.removeDate && pendingMove?.job) {
            fetchRef.current?.();
        }
        setShowConfirmation(false);
        setPendingMove(null);
        setActiveId(null);
        setPastDateWarning(false);
    };

    // ── Date picker ───────────────────────────────────────────────────────────
    const handleDatePickerSelect = (date) => {
        if (!date) return;
        const today = getAustralianDate();
        const currentWS = startOfWeek(today, { weekStartsOn: 1 });
        const selectedWS = startOfWeek(date, { weekStartsOn: 1 });
        setWeekOffset(differenceInWeeks(selectedWS, currentWS));
        setShowDatePicker(false);
        setDatePickerValue(null);
    };

    // ── Confirm-checkbox handlers ─────────────────────────────────────────────
    const handleConfirmToggle = (job, newValue) => {
        setPendingConfirm({ job, newValue });
        setShowConfirmCheck(true);
    };

    const handleConfirmCheckConfirm = async () => {
        if (!pendingConfirm) return;
        setConfirmCheckLoading(true);
        const { job, newValue } = pendingConfirm;
        const quoteId = job.quote_info?.id;
        try {
            const { error } = await updateSiteMeasureConfirmed(quoteId, newValue);
            if (error) throw error;

            // Patch the confirmed flag in local state without a full refetch
            const patcher = j =>
                j.schedule_row_uuid === job.schedule_row_uuid
                    ? { ...j, quote_info: { ...j.quote_info, site_measure_confirmed: newValue } }
                    : j;

            setScheduleData(prev => {
                const next = {};
                for (const day of Object.keys(prev)) next[day] = prev[day].map(patcher);
                return next;
            });
            setUnscheduledJobs(prev => prev.map(patcher));

            createJobActionLog({
                job_id: job.id,
                user_id: userProfile?.id,
                user_name: userProfile?.full_name || userProfile?.username || userProfile?.email || 'Unknown',
                user_role: userProfile?.role,
                action: newValue === true ? 'site_measure_complete_checked' : 'site_measure_complete_removed',
                details: `${job?.quote_info?.child_unique_id} (${job?.quote_info?.quote_type}) - Site measure ${newValue === true ? "completed checked." : "completed unchecked."}`,
                quote_type: job?.quote_info?.quote_type || null,
                action_origin: 'schedule-sitemeasure',
                company_id: company.id
            }).catch(() => { });
        } catch (err) {
            console.error('Failed to update site_measure_confirmed:', err);
            Toast.error('Failed to update confirmation status');
        } finally {
            setConfirmCheckLoading(false);
            setShowConfirmCheck(false);
            setPendingConfirm(null);
        }
    };

    const handleConfirmCheckCancel = () => {
        setShowConfirmCheck(false);
        setPendingConfirm(null);
    };

    // ── Render helpers ────────────────────────────────────────────────────────
    const renderScheduleCard = useCallback((job) => (
        <SortableCard
            key={job.schedule_row_uuid}
            job={job}
            isMobile={isMobile}
            onMobileMove={setMobileMoveJob}
            userProfile={userProfile}
            isDisabled={userProfile?.role !== 'admin' && userProfile?.role !== 'super_admin'}
            userRole={userProfile?.role}
            companySlug={companySlug}
            navigate={navigate}
            onConfirmToggle={handleConfirmToggle}
            openNotesBubble={openNotesBubble}
            setOpenNotesBubble={setOpenNotesBubble}
            setScheduleData={setScheduleData}
            setUnscheduledJobs={setUnscheduledJobs}
        />
    ), [
        isMobile,
        userProfile,
        companySlug,
        navigate,
        handleConfirmToggle,
        openNotesBubble,
    ]);

    const draggedJob =
        draggedItemCache ||
        (activeId
            ? [...Object.values(scheduleData).flat(), ...unscheduledJobs].find(j => j.schedule_row_uuid === activeId)
            : null);

    // ── JSX ───────────────────────────────────────────────────────────────────
    return (
        <div className="site-measure-page contents space-y-8">
            <DndContext
                sensors={isMobile ? [] : sensors}
                collisionDetection={siteMeasureCollision}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                measuring={{ droppable: { strategy: MeasuringStrategy.BeforeDragging } }}
            >
                <>
                    {/* ── Page header ──────────────────────────────────────────── */}
                    <div className="site-measure-header flex flex-row w-full justify-between mb-2">
                        <div className="flex flex-col items-start gap-4">
                            <BackButton
                                text="Back to Schedule Hub"
                                variant="ghost"
                                size="sm"
                                iconType="chevron"
                                className="text-blue-600 hover:text-blue-800"
                            />
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
                                    <Ruler className="w-7 h-7" />
                                    Site Measure Schedule
                                </h1>
                                <p className="text-gray-600 mt-1">
                                    {isMobile ? 'Tap Move on a job to choose a new site measure date' : 'Schedule and manage site measure visits — drag cards between days to reschedule'}
                                </p>
                            </div>
                        </div>
                        {/* ── Schedule activity log ── */}
                        <ScheduleActivityLog actionOrigin="schedule-sitemeasure" />
                    </div>

                    {/* ── Week navigation ──────────────────────────────────────── */}
                    <div
                        className={`site-measure-toolbar sticky top-0 z-[100] bg-white shadow-sm border border-gray-200 p-4 ${isScrolled ? 'rounded-none' : 'rounded-xl'}`}
                    >
                        {/* Top row: week nav buttons + week label */}
                        <div className="flex items-center justify-between gap-4">
                            <button
                                onClick={() => setWeekOffset(w => w - 4)}
                                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all font-medium group"
                                title="Back 4 weeks"
                            >
                                <FiChevronsLeft className="w-5 h-5 group-hover:scale-110 transition-transform" />
                                <span className="hidden sm:inline">Month</span>
                            </button>

                            <button
                                onClick={() => setWeekOffset(w => w - 1)}
                                className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all group"
                            >
                                <FiChevronLeft className="w-5 h-5 group-hover:scale-110 transition-transform" />
                                <span className="hidden md:inline text-sm">Week</span>
                            </button>

                            {/* Clickable week display */}
                            <button
                                onClick={() => { setDatePickerValue(getCurrentWeekStart()); setShowDatePicker(true); }}
                                className="flex-1 text-center hover:bg-gray-50 rounded-lg p-2 transition-colors group"
                                title="Click to select date"
                            >
                                <div className="flex flex-col gap-1">
                                    <div className="flex items-center justify-center gap-2">
                                        <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                                            {getMonthLabel()}
                                        </h2>
                                        {weekOffset === 0 && (
                                            <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">
                                                This Week
                                            </span>
                                        )}
                                        {weekOffset < 0 && (
                                            <span className="bg-gray-200 text-gray-700 text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">
                                                {Math.abs(weekOffset)} week{Math.abs(weekOffset) !== 1 ? 's' : ''} ago
                                            </span>
                                        )}
                                        {weekOffset > 0 && (
                                            <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">
                                                In {weekOffset} week{weekOffset !== 1 ? 's' : ''}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-600 font-medium group-hover:text-blue-600 transition-colors">
                                        {getWeekLabel()}
                                    </p>
                                    <p className="text-xs text-gray-400 group-hover:text-blue-500 transition-colors">
                                        Click to select date
                                    </p>
                                </div>
                            </button>

                            <button
                                onClick={() => setWeekOffset(w => w + 1)}
                                className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all group"
                            >
                                <span className="hidden md:inline text-sm">Week</span>
                                <FiChevronRight className="w-5 h-5 group-hover:scale-110 transition-transform" />
                            </button>

                            <button
                                onClick={() => setWeekOffset(w => w + 4)}
                                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all font-medium group"
                                title="Forward 4 weeks"
                            >
                                <span className="hidden sm:inline">Month</span>
                                <FiChevronsRight className="w-5 h-5 group-hover:scale-110 transition-transform" />
                            </button>
                        </div>

                        {/* Bottom row: scroll-reveal title, search, controls */}
                        <div className="mt-3 pt-3 border-t flex flex-row justify-between items-center gap-4 flex-wrap">
                            <div className="flex flex-row gap-3 items-center flex-wrap">
                                {/* Scroll-reveal title */}
                                <div
                                    className={`flex flex-row items-center gap-1 text-lg font-bold text-nowrap overflow-hidden transition-all ease-in-out duration-500 ${isScrolled ? 'max-w-[260px]' : 'max-w-0'}`}
                                >
                                    <Ruler className="w-4 h-4 flex-shrink-0" />
                                    Site Measure
                                </div>

                                {/* Search */}
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                        placeholder="Search Job ID, Customer, Address…"
                                        className="w-64 sm:w-80 px-4 py-2 pl-10 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                                    />
                                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    {searchQuery && (
                                        <button
                                            onClick={() => setSearchQuery('')}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                        >
                                            <FiX className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>

                                {/* Saturday toggle */}
                                <button
                                    onClick={() => setShowSaturday(p => !p)}
                                    className={`flex items-center gap-1.5 px-3 py-1 rounded-md border text-sm font-medium transition-colors ${showSaturday
                                        ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                                        : 'border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100'
                                        }`}
                                >
                                    {showSaturday ? 'Hide Saturday' : 'Saturday Hidden'}
                                </button>
                            </div>

                            {weekOffset !== 0 && (
                                <button
                                    onClick={() => setWeekOffset(0)}
                                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all font-medium text-sm shadow-sm hover:shadow flex items-center gap-2"
                                >
                                    <FiCalendar className="w-4 h-4" />
                                    Jump to This Week
                                </button>
                            )}
                        </div>
                    </div>

                    {/* ── Day columns grid ──────────────────────────────────────── */}
                    {isLoading ? (
                        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${showSaturday ? 'xl:grid-cols-6' : 'xl:grid-cols-5'}`}>
                            {days.map(({ label, key }, i) => (
                                <DayColumnSkeleton key={key} dayLabel={label} dayDate={getDayDate(i)} />
                            ))}
                        </div>
                    ) : (
                        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${showSaturday ? 'xl:grid-cols-6' : 'xl:grid-cols-5'}`}>
                            {days.map(({ label, key }, dayIndex) => {
                                const allDayJobs = scheduleData[key] || [];
                                const filteredJobs = debouncedSearch.trim()
                                    ? allDayJobs.filter(matchesSearch)
                                    : allDayJobs;
                                const sortedIds = allDayJobs.map(j => j.schedule_row_uuid);
                                const dayObj = getDayDateObject(dayIndex);
                                return (
                                    <VirtualDroppableDay
                                        key={key}
                                        dayKey={key}
                                        dayLabel={label}
                                        dayDate={getDayDate(dayIndex)}
                                        dayIsPast={isDateInPast(dayObj)}
                                        dayIsToday={isDayToday(dayIndex)}
                                        dayHolidays={getHolidaysForDate(dayObj)}
                                        jobs={filteredJobs}
                                        sortedIds={sortedIds}
                                        jobCount={filteredJobs.length}
                                        isFiltered={!!debouncedSearch.trim()}
                                        renderCard={renderScheduleCard}
                                    />
                                );
                            })}
                        </div>
                    )}

                    {/* ── Unscheduled section ───────────────────────────────────── */}
                    {isLoading ? (
                        <UnscheduledSectionSkeleton />
                    ) : (
                        <DroppableUnscheduled>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">
                                    Unscheduled Site Measures
                                </h3>
                                <div className="flex items-center gap-2">
                                    <span className="bg-orange-100 text-orange-800 text-sm font-medium px-3 py-1 rounded-full">
                                        {(debouncedSearch.trim()
                                            ? unscheduledJobs.filter(matchesSearch)
                                            : unscheduledJobs
                                        ).length} cards
                                        {debouncedSearch.trim() && (
                                            <span className="text-orange-600 ml-1">(filtered)</span>
                                        )}
                                    </span>
                                    <span className="text-xs text-gray-500 hidden sm:inline">
                                        Drop here to unschedule
                                    </span>
                                </div>
                            </div>
                            {(() => {
                                const filtered = debouncedSearch.trim()
                                    ? unscheduledJobs.filter(matchesSearch)
                                    : unscheduledJobs;
                                const sortedIds = unscheduledJobs.map(j => j.schedule_row_uuid);
                                return (
                                    <VirtualUnscheduledGrid
                                        jobs={filtered}
                                        sortedIds={sortedIds}
                                        isFiltered={!!debouncedSearch.trim()}
                                        renderCard={renderScheduleCard}
                                    />
                                );
                            })()}
                        </DroppableUnscheduled>
                    )}

                    {/* ── Legend ───────────────────────────────────────────────── */}
                    <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-3 text-sm">Legend</h4>
                        <div className="flex flex-wrap gap-4 text-xs">
                            <div className="flex items-center gap-4">
                                <span className="font-semibold text-gray-600">Card types:</span>
                                {['roof', 'wall', 'floor'].map(k => {
                                    const v = QUOTE_CONFIG[k];
                                    return (
                                        <span key={k} className={`px-2 py-0.5 rounded ${v.bg} ${v.text} font-semibold`}>
                                            {v.label}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* ── Confirmation popup ────────────────────────────────────── */}
                    <ConfirmationPopup
                        isOpen={showConfirmation}
                        title={pastDateWarning ? '⚠️ Moving to Past Date' : 'Confirm Site Measure Date'}
                        message={
                            pendingMove
                                ? pastDateWarning
                                    ? `Warning: You are scheduling "${pendingMove.job.schedule_row_id}" (${pendingMove.job.sm_type}) site measure to ${pendingMove.newDate?.toLocaleDateString('en-AU')}, which is in the past.\n\nAre you sure you want to proceed?`
                                    : pendingMove.removeDate
                                        ? `Remove the site measure date for "${pendingMove.job.schedule_row_id}" (${pendingMove.job.sm_type})? This will move it back to unscheduled.`
                                        : `Schedule site measure for "${pendingMove.job.schedule_row_id}" (${pendingMove.job.sm_type}) on ${pendingMove.newDate?.toLocaleDateString('en-AU')}?`
                                : ''
                        }
                        confirmText={
                            pastDateWarning
                                ? 'Yes, Schedule to Past Date'
                                : pendingMove?.removeDate
                                    ? 'Remove Date'
                                    : 'Schedule Site Measure'
                        }
                        cancelText="Cancel"
                        onConfirm={handleConfirmMove}
                        onCancel={handleCancelMove}
                    />

                    {/* ── Date picker modal ─────────────────────────────────────── */}
                    {showDatePicker && (
                        <div className="mobile-dialog-shell fixed inset-0 !mt-0 bg-white bg-opacity-50 flex items-center justify-center z-[200] p-4">
                            <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
                                <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-blue-100">
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900">Select Week</h3>
                                        <p className="text-sm text-gray-600 mt-1">
                                            Choose any date to jump to that week
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setShowDatePicker(false)}
                                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors"
                                    >
                                        <FiX className="w-6 h-6" />
                                    </button>
                                </div>
                                <div className="p-6 flex justify-center">
                                    <DatePicker
                                        selected={datePickerValue}
                                        onChange={handleDatePickerSelect}
                                        inline
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                        calendarStartDay={1}
                                        dateFormat="dd/MM/yyyy"
                                        filterDate={d => d.getDay() !== 0}
                                        className="date-picker-custom"
                                    />
                                </div>
                                <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
                                    <button
                                        onClick={() => setShowDatePicker(false)}
                                        className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                                    >
                                        Cancel
                                    </button>
                                    {weekOffset !== 0 && (
                                        <button
                                            onClick={() => { setWeekOffset(0); setShowDatePicker(false); }}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2"
                                        >
                                            <FiCalendar className="w-4 h-4" />
                                            Jump to This Week
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </>

                {/* ── Site measure confirmed popup ──────────────────────────────── */}
                <ConfirmationPopup
                    isOpen={showConfirmCheck}
                    title={pendingConfirm?.newValue ? 'Confirm Site Measure' : 'Unconfirm Site Measure'}
                    message={
                        pendingConfirm
                            ? pendingConfirm.newValue
                                ? `Mark "${pendingConfirm.job.schedule_row_id}" (${pendingConfirm.job.sm_type}) as confirmed? This indicates the site measure has been confirmed with the client.`
                                : `Remove confirmation for "${pendingConfirm.job.schedule_row_id}" (${pendingConfirm.job.sm_type})? This will mark it as unconfirmed.`
                            : ''
                    }
                    confirmText={pendingConfirm?.newValue ? 'Mark Confirmed' : 'Mark Unconfirmed'}
                    cancelText="Cancel"
                    type={pendingConfirm?.newValue ? 'success' : 'warning'}
                    loading={confirmCheckLoading}
                    onConfirm={handleConfirmCheckConfirm}
                    onCancel={handleConfirmCheckCancel}
                />

                {/* ── Drag overlay (floating clone while dragging) ──────────────── */}
                <DragOverlay dropAnimation={dropAnimationConfig} modifiers={[snapCenterToCursor]}>
                    {draggedJob ? (
                        <div className="opacity-90 w-64">
                            <SortableCard
                                job={draggedJob}
                                userProfile={userProfile}
                                isDisabled={true}
                                userRole={userProfile?.role}
                                companySlug={companySlug}
                                navigate={navigate}
                            />
                        </div>
                    ) : null}
                </DragOverlay>
                {mobileMoveJob && <MobileScheduleMoveDialog companyState={company?.state} loadCounts={month => loadWeeklyCalendarCounts(month, weekStart => getSiteMeasureScheduled(company.id, { weekStart }))} title="Move site measure"
                    allowUnscheduled={!!mobileMoveJob.quote_info?.site_measure_date}
                    jobLabel={mobileMoveJob.schedule_row_id || mobileMoveJob.unique_id}
                    onClose={() => setMobileMoveJob(null)} onSelect={newDate => {
                        if (!['admin', 'super_admin'].includes(userProfile?.role)) return;
                        const isPast = newDate ? isDateInPast(newDate) : false;
                        setPendingMove({ job: mobileMoveJob, newDate, removeDate: !newDate,
                            dayKey: newDate?.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' }), isPastDate: isPast });
                        setPastDateWarning(isPast); setMobileMoveJob(null); setShowConfirmation(true);
                    }} />}
            </DndContext>
        </div>
    );
};

export default SiteMeasureSchedule;
